<template>
  <v-card>
    <v-card-title>
      <span class="headline-2">{{ addOffersToDealMode ? $i18n.useDealPicker.ADD_OFFERS_TO_DEAL : $i18n.useDealPicker.LOAD_DEAL }}</span>
    </v-card-title>
    <v-divider/>
    <v-card-text>
      <v-container fluid grid-list-lg>
        <div class="body-1 opacity-90 mb-6 mt-4 tracking-normal " v-if="modalData.additionalInfo">
          {{ modalData.additionalInfo }}
        </div>
        <raiffeisen-access-info
          v-if="isRaiffeisenOfferSelected && !hasAccessToRaiffeisen"
          :selected-offers-count="selectedOffersCount"/>
        <client-autocomplete
            :singular="true"
            :create="true"
            :is-company="false"
            v-model="selectedClient"
            :disabled="isRaiffeisenOfferSelected && selectedOffersCount === 1 && !hasAccessToRaiffeisen"
            @input="onClientPicked($event)"/>
        <template v-if="selectedClient">
          <fp-radio-group
              v-if="clientHasDeals"
              prepend-icon="none"
              class="mb-4 mt-3"
              row
              v-model="isExistingDeal"
              :items="dealRadioItems"/>
          <v-window v-model="isExistingDeal" class="mb-4">
            <v-window-item :value="true">
              <v-layout wrap class="py-2">
                <fp-select
                    xs12
                    :id="$cy.reportModalDealPicker"
                    :items="clientDeals"
                    v-model="selectedDeal"
                    :label="$i18n.useDealPicker.CHOOSE_DEAL"
                    prepend-icon="none"
                    item-name="name_pl"
                    item-key="type"/>
              </v-layout>
            </v-window-item>
            <v-window-item :value="false">
              <deal-badge class="ml-8" :readonly="true" :deal="newDeal"/>
            </v-window-item>
          </v-window>
          <deal-from-partner-autocomplete
            v-if="!isExistingDeal"
            v-model="newDeal.partner"
              :id="$cy.reportModalPartnerPicker"/>
        </template>
      </v-container>
    </v-card-text>
    <v-divider/>
    <v-card-actions>
      <fp-btn v-if="addOffersToDealMode" primary :disabled="!selectedClient" :click="() => addOffersToDeal()">
        {{ isRaiffeisenOfferSelected && selectedOffersCount > 1 && !hasAccessToRaiffeisen ?
          $i18n.useSimulationResult.ADD_OTHER_OFFERS_TO_DEAL :
          $i18n.useDealPicker.ADD_OFFERS_TO_DEAL}}
      </fp-btn>
      <fp-btn v-else primary :disabled="!selectedClient" :click="() => addSimulationToDeal()">
        {{ $i18n.useDealPicker.LOAD_DEAL }}
      </fp-btn>
    </v-card-actions>
  </v-card>
</template>
<script lang="ts">
import {Component} from 'vue-property-decorator';
import {VueModalComponent} from 'fp-components';
import Deal from '@/models/Deal';
import {IMultiformDictionary} from '@/modules/multiForm/shared/IMultiformDictionary';
import ClientAutocomplete from '@/components/inputs/ClientAutocomplete.vue';
import DealFromPartnerAutocomplete from '@/components/inputs/DealFromPartnerAutocomplete.vue';
import DealsApi from '@/modules/deals/services/DealsApi';
import Client from '@/models/Client';
import SimulationApi from '@/modules/calculator/services/SimulationApi';
import {I18NGetter} from '@/services/enumTranslator/I18NGetter';
import { BankSymbol } from '@/commons/dictionaries/BankSymbol';
import { Offer } from '@/models/simulation/Simulation';
import {i18nFormatter} from '@/services/StringFormatter';
const RaiffeisenAccessInfo = () => import(/* webpackChunkName: "RaiffeisenAccessInfo" */ '@/components/calculator/results/RaiffeisenAccessInfo.vue');

@Component({
  components: {DealFromPartnerAutocomplete, ClientAutocomplete, RaiffeisenAccessInfo,},
})
export default class SimulationDealPicker extends VueModalComponent<Deal> {
  isExistingDeal: boolean = false;
  selectedClient: Nullable<Client> = null;
  selectedDeal: Nullable<Deal> = null;
  partnerId: Nullable<number> = null;

  newDeal: Deal = new Deal({
    productType: this.modalData.productType,
    simulationId: this.modalData.simulationId,
  });

  addOffersToDealMode: boolean = !!this.modalData.offers;

  clientDeals: IMultiformDictionary<Deal | null> = [];

  readonly dealRadioItems: IMultiformDictionary<boolean> = [
    {type: true, name_pl: I18NGetter().useDealPicker.EXISTING_DEAL,},
    {type: false, name_pl: I18NGetter().useDealPicker.NEW_DEAL,},
  ];

  async onClientPicked(client: Nullable<Client>) {
    if (client) {
      if (client?.partnerId) {
        this.partnerId = client.partnerId;
      }
      const response: Deal[] = await DealsApi.getDealsByClientId(client!.id);
      const activeDeals = response.filter(deal => deal.isPending && deal.productType === this.modalData.productType);
      if (activeDeals.length > 0) {
        this.selectedDeal = activeDeals[0];
        this.isExistingDeal = true;
      } else {
        this.isExistingDeal = false;
      }
      this.clientDeals = activeDeals
        .map(deal => {
          return {
            name_pl: deal.name,
            type: deal,
          };
        });
      this.newDeal.name = i18nFormatter(
        I18NGetter().useDealPicker.NEW_TRANSLATION_NEW_DEAL_NAME, {clientName: client.displayName,}
      );
      this.newDeal.clientIds = [client.id,];
    } else {
      this.selectedDeal = null;
      this.clientDeals = [];
      console.warn('There is no selected client', client);
    }
  }

  async createOrUpdateDeal(): Promise<Deal> {
    if (this.isExistingDeal) {
      return this.selectedDeal?.simulationId
        ? this.selectedDeal!
        : DealsApi.updateDeal({...this.selectedDeal!, simulationId: this.modalData.simulationId,});
    } else {
      return DealsApi.addDeal(this.newDeal);
    }
  }

  async addSimulationToDeal() {
    try {
      const deal = await this.createOrUpdateDeal();
      this.$snackbarService.openSuccessSnackbar(I18NGetter().useDealPicker.DEAL_LOADED);
      this.closeModal(deal);
    } catch (e) {
      this.errorHandler(e, I18NGetter().useDealPicker.LOAD_DEAL_ERROR);
    }
  }

  async addOffersToDeal() {
    const {simulationId, userInput, offers,} = this.modalData;
    const offersToSave: Array<Offer> =
      this.isRaiffeisenOfferSelected && this.selectedOffersCount > 1 && !this.hasAccessToRaiffeisen
        ? offers.filter((offer: Offer) => offer.info.bank !== BankSymbol.RAIFFEISEN) : offers;
    try {
      const {id,}: Deal = await this.createOrUpdateDeal();
      await SimulationApi.addOffersToDeal(id, simulationId, userInput, offersToSave);
      this.$snackbarService.openSuccessSnackbar(I18NGetter().useDealPicker.OFFERS_ADDED);
      if (!this.modalData.additionalInfo) {
        this.$goto.deal(id);
      }

      this.closeModal();
    } catch (e) {
      this.errorHandler(e, I18NGetter().useDealPicker.ADD_OFFERS_TO_DEAL_ERROR);
    }
  }

  get clientHasDeals(): boolean {
    return this.clientDeals.length > 0;
  }

  get hasAccessToRaiffeisen(): boolean {
    return this.$user.privileges.hasAccessToRaiffeisen;
  };

  get isRaiffeisenOfferSelected(): boolean {
    return this.modalData.offers?.some((offer: Offer) => offer.info.bank === BankSymbol.RAIFFEISEN) || false;
  };

  get selectedOffersCount(): number {
    return this.modalData.offers?.length || 0;
  };
}
</script>
<style scoped lang="scss">
</style>
